// 문진 v2 (DRT-7186): 서버가 내려주는 질문지 형식과 show_if·alerts 평가기.
// 규칙의 원본은 서버 consultation/intake_v2.py 이고, 공용 벡터(intake_v2_vectors.json)로 같은 결과를 확인한다.
// 순수 함수만 둔다(외부 import 없음): 개발 중 node 스크립트로 벡터를 돌릴 수 있게.

export const INTAKE_UNKNOWN = 'unknown'

export type IntakeAnswer = string | number | string[]
export type IntakeAnswers = Record<string, IntakeAnswer>

export type IntakeCondition = Readonly<Record<string, unknown>>

export type IntakeAlertLevel = 'urgent' | 'emergency'

// 보기마다 show_if 가 있을 수 있다 (같은 문법·문맥). 거짓이면 그 보기를 그리지 않고, 남은 답에서도 그 값을 뺀다
export type IntakeOptionV2 = Readonly<{
  value: string
  label: string
  show_if?: IntakeCondition | null
}>

export type IntakeQuestionV2 = Readonly<{
  id: string
  question: string
  type: 'single' | 'multi' | 'text' | 'number'
  options: IntakeOptionV2[]
  show_if: IntakeCondition | null
  // true 면 답(또는 "I don't know")이 있어야 Next 를 누를 수 있다. 없으면 예전처럼 건너뛸 수 있다
  required?: boolean
  // 처음으로 when 이 참인 문구를 제목으로 쓴다 (없으면 question). 예: onset 은 증상 이름을 넣은 문구
  question_variants?: Readonly<{ when: IntakeCondition; question: string }>[]
  // 다중 선택: 같은 묶음의 값은 하나만 고를 수 있다 (예: 통증 정도). exclusive(none·unknown)와 별개
  exclusive_groups?: string[][]
  unknown: boolean
  unknown_label: string
  exclusive: string[]
  alerts: Readonly<{ level: IntakeAlertLevel; when: IntakeCondition }>[]
  placeholder?: string
  max_length?: number
  unit?: string
  min?: number
  max?: number
  hint_from?: string
  // 다중 선택: 아무것도 고르지 않고 Next 를 누르면 이 값으로 답한다(그 값의 보기는 목록에서 숨긴다)
  empty_means?: string
  // empty_means 가 있을 때 "Select all that apply." 아래에 보여 줄 안내
  empty_hint?: string
  // 숫자 질문: 프로필의 이 값으로 미리 채운다 / 제출 후 이 값에 저장한다 ('pet.weight')
  prefill_from?: string
  save_to?: string
}>

// 주요 증상 메뉴 한 칸 (DRT-7185). 보여 줄 순서대로 온다. is_new 면 others 문진 + category 로 보낸다
export type ConcernMeta = Readonly<{
  value: string
  label: string
  keywords: string
  description: string
  icon: string | null
  is_new: boolean
}>

export type IntakeSpecV2 = Readonly<{
  version: 2
  rules_version: number
  concern_order: string[]
  new_concerns: Readonly<{ value: string; label: string; description: string }>[]
  // 주요 증상 메뉴 (이름·키워드·설명·아이콘). 없으면 예전 목록(new_concerns + 기존 증상)
  concern_meta?: ConcernMeta[]
  species_by_kind: Record<string, string>
  // 펫 gender 코드('0','1') → 'male' | 'female'. 그 밖의 코드(2=Unknown)·없음은 성별 모름
  sex_by_gender?: Record<string, string>
  flows: Record<string, string[]>
  questions: Record<string, IntakeQuestionV2>
  alerts: Readonly<{
    emergency: Readonly<{ title: string; body: string }>
    urgent: Readonly<{ title: string; body: string }>
    actions: Readonly<{ find_clinic: string; continue: string }>
  }>
  details: Readonly<{ question: string; placeholder: string }>
  // 이 날짜보다 이른 생일은 '모름'으로 본다 (SmartThings 기본값 1900-01-01)
  unknown_born_before?: string
  // false 면 문진 중 응급 안내를 띄우지 않는다 (규칙 평가는 계속한다). 없으면 띄운다
  show_alerts?: boolean
}>

/** 문진 중 응급 안내를 띄우는지 (서버 show_alerts, 없으면 예전처럼 띄운다) */
export const showsIntakeAlerts = (spec: Pick<IntakeSpecV2, 'show_alerts'>) =>
  spec.show_alerts !== false

/** 숫자 질문을 프로필 값으로 미리 채우는 출처 (질문의 prefill_from, 'pet.weight'). 안 채우면 null */
export const numberPrefillSource = (question: Pick<IntakeQuestionV2, 'type' | 'prefill_from'>) =>
  question.type === 'number' && question.prefill_from ? question.prefill_from : null

// 서버 PetWeight 의 단위 표기(lbs 기본, validate_unit 으로 정규화)와 질문 단위(lb)를 같은 단위로 본다
const WEIGHT_UNITS: Record<string, 'lb' | 'kg'> = {
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  kg: 'kg',
  kgs: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
}
const LB_PER_KG = 2.20462

/** 몸무게를 질문의 단위로 바꾼다(소수 첫째 자리). 모르는 단위·잘못된 값이면 null */
export const convertWeight = (
  value: number | string | null | undefined,
  fromUnit: string | null | undefined,
  toUnit: string | null | undefined
): number | null => {
  const amount = typeof value === 'string' ? Number(value) : value
  if (amount === null || amount === undefined || !Number.isFinite(amount) || amount < 0) return null
  const from = WEIGHT_UNITS[(fromUnit ?? '').toLowerCase()]
  const to = WEIGHT_UNITS[(toUnit ?? '').toLowerCase()]
  if (!from || !to) return null
  const converted = from === to ? amount : from === 'kg' ? amount * LB_PER_KG : amount / LB_PER_KG
  return Math.round(converted * 10) / 10
}

export type IntakeContext = Readonly<{
  // flows 키: 기존 main concern, 신규 증상이면 category 값
  concern: string
  answers: IntakeAnswers
  species: string | null
  ageWeeks: number | null
  // 'male' | 'female', 모르면 null
  sex: string | null
  // 중성화 여부, 모르면 null
  neutered: boolean | null
}>

const valuesOf = (value: IntakeAnswer | undefined | null): unknown[] => {
  if (value === undefined || value === null || value === '') return []
  return Array.isArray(value) ? value : [value]
}

const isEmptyAnswer = (value: IntakeAnswer | undefined | null) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const list = (value: unknown) => (Array.isArray(value) ? (value as unknown[]) : [])

/** show_if / alerts.when 평가. 서버 evaluate() 와 같은 의미 */
export const evaluateCondition = (
  cond: IntakeCondition | null | undefined,
  ctx: IntakeContext
): boolean => {
  if (!cond || Object.keys(cond).length === 0) return true
  if ('all' in cond) return list(cond.all).every(c => evaluateCondition(c as IntakeCondition, ctx))
  if ('any' in cond) return list(cond.any).some(c => evaluateCondition(c as IntakeCondition, ctx))
  if ('not' in cond) return !evaluateCondition(cond.not as IntakeCondition, ctx)
  if ('q' in cond) {
    const values = valuesOf(ctx.answers[cond.q as string])
    if ('in' in cond) return values.some(v => list(cond.in).includes(v))
    if ('not_in' in cond) return !values.some(v => list(cond.not_in).includes(v))
    if ('has_any' in cond) return values.some(v => list(cond.has_any).includes(v))
    if ('answered' in cond) return values.length > 0 === Boolean(cond.answered)
    throw new Error(`unknown q condition: ${JSON.stringify(cond)}`)
  }
  if ('concern' in cond) return list(cond.concern).includes(ctx.concern)
  if ('concern_not' in cond) return !list(cond.concern_not).includes(ctx.concern)
  if ('species' in cond) return ctx.species !== null && list(cond.species).includes(ctx.species)
  if ('species_not' in cond)
    return ctx.species === null || !list(cond.species_not).includes(ctx.species)
  if ('pet' in cond) {
    if (cond.pet === 'sex') {
      if (ctx.sex === null) return Boolean(cond.if_unknown)
      return list(cond.in).includes(ctx.sex)
    }
    if (cond.pet === 'neutered') {
      if (ctx.neutered === null) return Boolean(cond.if_unknown)
      return list(cond.in).includes(ctx.neutered)
    }
    if (cond.pet !== 'age_weeks') throw new Error(`unknown pet field: ${JSON.stringify(cond)}`)
    if (ctx.ageWeeks === null) return Boolean(cond.if_unknown)
    if ('lt' in cond) return ctx.ageWeeks < (cond.lt as number)
    if ('gte' in cond) return ctx.ageWeeks >= (cond.gte as number)
    throw new Error(`unknown pet condition: ${JSON.stringify(cond)}`)
  }
  throw new Error(`unknown condition: ${JSON.stringify(cond)}`)
}

/** 평가 중 모르는 조건을 만났을 때 (질문지가 클라이언트보다 새 문법을 쓴 경우) */
export type IntakeEvalErrorHandler = (
  error: unknown,
  questionId: string,
  where: 'show_if' | 'alert' | 'option' | 'question_variant'
) => void

/** 조건 평가. 모르는 조건이면 onError 로 알리고 fallback 을 돌려준다 */
const evaluateOr = (
  cond: IntakeCondition | null | undefined,
  ctx: IntakeContext,
  fallback: boolean,
  report: (error: unknown) => void
) => {
  try {
    return evaluateCondition(cond, ctx)
  } catch (error) {
    report(error)
    return fallback
  }
}

/** 보기의 show_if 가 거짓이라 숨기는 값들 (모르는 조건이면 보인다) */
export const hiddenOptionValues = (
  question: Pick<IntakeQuestionV2, 'id' | 'options'>,
  ctx: IntakeContext,
  onError?: IntakeEvalErrorHandler
) =>
  question.options
    .filter(
      option =>
        option.show_if &&
        !evaluateOr(option.show_if, ctx, true, e => onError?.(e, question.id, 'option'))
    )
    .map(option => option.value)

/** 보이는 제목: question_variants 중 처음으로 when 이 참인 문구, 없으면 question (서버 question_text 와 같은 규칙) */
export const questionTextOf = (
  question: Pick<IntakeQuestionV2, 'id' | 'question' | 'question_variants'>,
  ctx: IntakeContext,
  onError?: IntakeEvalErrorHandler
) =>
  question.question_variants?.find(variant =>
    evaluateOr(variant.when, ctx, false, e => onError?.(e, question.id, 'question_variant'))
  )?.question ?? question.question

/** 숨긴 보기의 값을 답에서 뺀다: 다중 선택은 그 값만, 단일 선택은 답이 없어진다 (서버 _visible_value) */
const withoutHidden = (value: IntakeAnswer | undefined, hidden: string[]) => {
  if (hidden.length === 0 || value === undefined) return value
  if (Array.isArray(value)) return value.filter(v => !hidden.includes(v))
  return typeof value === 'string' && hidden.includes(value) ? undefined : value
}

/**
 * 흐름을 순서대로 보며 보이는 질문과, 보이는 질문의 답만 남긴 답(kept)을 돌려준다 (서버 resolve() 와 같은 순서·문맥).
 * 조건은 앞 질문만 참조하고, 숨긴 질문의 답·흐름 밖 질문의 답은 '안 답함'으로 본다.
 * 보기의 show_if 로 숨긴 값은 kept 에서 빠지고(hiddenOptions), question_variants 가 있는 질문은 보이는 제목(questionText)을 함께 준다.
 * 모르는 조건은 화면을 깨지 않게 '보임'으로 처리하고 onError 로 알린다(fail-open).
 */
export const resolveIntake = (
  spec: IntakeSpecV2,
  concern: string,
  answers: IntakeAnswers,
  species: string | null,
  ageWeeks: number | null,
  sex: string | null,
  neutered: boolean | null,
  onError?: IntakeEvalErrorHandler
) => {
  const kept: IntakeAnswers = {}
  const visible: IntakeQuestionV2[] = []
  const hiddenOptions: Record<string, string[]> = {}
  const questionText: Record<string, string> = {}
  const ctx: IntakeContext = { concern, answers: kept, species, ageWeeks, sex, neutered }
  for (const id of spec.flows[concern] ?? []) {
    const question = spec.questions[id]
    if (!question) continue
    let shown = true
    try {
      shown = evaluateCondition(question.show_if, ctx)
    } catch (error) {
      onError?.(error, id, 'show_if')
    }
    if (!shown) continue
    visible.push(question)
    // 보기·제목 조건도 이 질문 앞까지의 답으로 평가한다
    const hidden = hiddenOptionValues(question, ctx, onError)
    if (hidden.length > 0) hiddenOptions[id] = hidden
    if (question.question_variants?.length)
      questionText[id] = questionTextOf(question, ctx, onError)
    const value = withoutHidden(answers[id], hidden)
    if (!isEmptyAnswer(value)) kept[id] = value as IntakeAnswer
  }
  return { visible, kept, hiddenOptions, questionText }
}

const ALERT_RANK: Record<IntakeAlertLevel, number> = { urgent: 1, emergency: 2 }

export const alertRank = (level: IntakeAlertLevel | null | undefined) =>
  level ? ALERT_RANK[level] : 0

export type IntakeAlertHit = Readonly<{
  level: IntakeAlertLevel
  // 그 단계를 처음 만든 규칙의 질문과, 규칙에 걸린 답 값 (Mixpanel 용)
  questionId: string
  option: string | null
}>

/** 규칙 안에서 이 질문을 보는 in/has_any 값과 답이 겹치는 첫 값. 없으면 답의 첫 값 */
const matchedOption = (cond: IntakeCondition, questionId: string, answers: IntakeAnswers) => {
  const listed: unknown[] = []
  const walk = (c: IntakeCondition) => {
    if ('all' in c) list(c.all).forEach(x => walk(x as IntakeCondition))
    else if ('any' in c) list(c.any).forEach(x => walk(x as IntakeCondition))
    else if ('not' in c) walk(c.not as IntakeCondition)
    else if (c.q === questionId) listed.push(...list(c.in), ...list(c.has_any))
  }
  walk(cond)
  const values = valuesOf(answers[questionId])
  const hit = values.find(v => listed.includes(v)) ?? values[0]
  return hit === undefined ? null : String(hit)
}

/**
 * 이 답(kept)으로 띄울 가장 높은 응급 안내. 흐름 순서대로 모든 질문의 규칙을 본다
 * (서버 alert_level() 과 같은 규칙, 공용 벡터의 alert). 같은 단계면 처음 걸린 규칙. 없으면 null.
 * 모르는 조건의 규칙은 건너뛰고 onError 로 알린다.
 */
export const getHighestAlertHit = (
  spec: IntakeSpecV2,
  ctx: IntakeContext,
  onError?: IntakeEvalErrorHandler
): IntakeAlertHit | null => {
  let best: IntakeAlertHit | null = null
  for (const id of spec.flows[ctx.concern] ?? []) {
    for (const rule of spec.questions[id]?.alerts ?? []) {
      if (alertRank(rule.level) <= alertRank(best?.level)) continue
      let hit = false
      try {
        hit = evaluateCondition(rule.when, ctx)
      } catch (error) {
        onError?.(error, id, 'alert')
      }
      if (hit)
        best = {
          level: rule.level,
          questionId: id,
          option: matchedOption(rule.when, id, ctx.answers),
        }
    }
  }
  return best
}

export const getHighestAlert = (spec: IntakeSpecV2, ctx: IntakeContext) =>
  getHighestAlertHit(spec, ctx)?.level ?? null

/** 펫 종류 → 종. 하위 종류(depth2) 이름을 먼저 보고, 없으면 상위(depth1). 종류가 없으면 null */
export const speciesOfKind = (
  spec: Pick<IntakeSpecV2, 'species_by_kind'>,
  kind: Readonly<{ depth1?: string | null; depth2?: string | null }> | null | undefined
): string | null => {
  if (!kind || (!kind.depth1 && !kind.depth2)) return null
  for (const name of [kind.depth2, kind.depth1]) {
    if (name && spec.species_by_kind[name]) return spec.species_by_kind[name]
  }
  return 'other'
}

/** 펫 gender 코드 → 'male' | 'female'. 0·1 말고(2=Unknown)·null·없음은 null */
export const sexOfGender = (
  spec: Pick<IntakeSpecV2, 'sex_by_gender'>,
  gender: number | string | null | undefined
): string | null =>
  gender === null || gender === undefined ? null : (spec.sex_by_gender?.[String(gender)] ?? null)

/** 생일 → 지금까지의 주 수. 모르거나(없음·잘못된 값·미래) null */
export const ageWeeksOf = (
  bornAt: string | null | undefined,
  unknownBornBefore?: string | null,
  now: number = Date.now()
) => {
  if (!bornAt) return null
  const born = new Date(bornAt).getTime()
  if (Number.isNaN(born)) return null
  // 생일을 안 넣은 펫의 기본값(1900-01-01)은 나이를 모르는 것으로 본다 (서버 UNKNOWN_BORN_BEFORE)
  if (unknownBornBefore && born < new Date(unknownBornBefore).getTime()) return null
  const weeks = (now - born) / (7 * 24 * 3600 * 1000)
  return weeks >= 0 ? weeks : null
}

export const isUnknownAnswer = (value: IntakeAnswer | undefined) =>
  value === INTAKE_UNKNOWN || (Array.isArray(value) && value.includes(INTAKE_UNKNOWN))

export const isIntakeSpecV2 = (spec: unknown): spec is IntakeSpecV2 =>
  !!spec && typeof spec === 'object' && (spec as { version?: unknown }).version === 2
